import { z } from 'zod';

import { setJwtCookie } from '~/features/cookies/utils/set-jwt-cookie';
import { publicEnv } from '~/features/environment/public';
import { logger } from '~/features/logging/logger';

import { QuickAuthenticationError } from '../errors';

export async function postSignup(email: string, captchaValue: string | undefined) {
  const response = await fetch(`${publicEnv.NEXT_PUBLIC_USER_URL}/signup_no_password`, {
    method: 'POST',
    headers: {
      ...(captchaValue && { 'X-Udacity-ReCAPTCHA-UserAPI': captchaValue }),
    },
    body: JSON.stringify({
      email: email,
    }),
  });

  const data = await response.json().catch(() => {});

  if (!response.ok) {
    if (isErrorData(data) && data.code === 409 && data.message.includes('already registered')) {
      logger.warn('Error email already exists', {
        file: 'src/features/quick-authentication/controllers/post-signup.ts',
        function: 'postSignup',
        statusCode: response.status,
        error: data,
      });

      throw new QuickAuthenticationError({
        name: 'EMAIL_EXISTS',
        message: 'Email already exists',
        cause: data,
      });
    }

    if (response.status === 429) {
      logger.warn('sign up captcha required', {
        file: 'src/features/quick-authentication/controllers/post-signup.ts',
        function: 'postSignup',
        statusCode: response.status,
        error: data,
      });

      throw new QuickAuthenticationError({
        name: 'CAPTCHA_REQUIRED',
        message: 'Complete the captcha to confirm you are not a robot.',
        cause: data,
      });
    }

    logger.fatal('Error signup_no_password', {
      file: 'src/features/quick-authentication/controllers/post-signup.ts',
      function: 'postSignup',
      statusCode: response.status,
      error: data,
    });

    throw new QuickAuthenticationError({
      name: 'SIGN_UP_ERROR',
      message: 'Error signup_no_password',
      cause: data,
    });
  }

  try {
    const { jwt } = z
      .object({
        jwt: z.string(),
      })
      .parse(data);

    setJwtCookie(jwt);
  } catch (error) {
    logger.fatal('Error parsing signup_no_password response', {
      file: 'src/features/quick-authentication/controllers/post-signup.ts',
      function: 'postSignup',
      error: error,
    });

    throw new QuickAuthenticationError({
      name: 'SIGN_UP_ERROR',
      message: 'Error parsing signup_no_password response',
      cause: error,
    });
  }
}

function isErrorData(data: unknown): data is { code: number; message: string } {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as { code: unknown }).code === 'number' &&
    typeof (data as { message: unknown }).message === 'string'
  );
}
